import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { downloadBlob, unwrapData } from "../lib/catalog";

function formatLabel(value, fallback = "-") {
  const text = String(value ?? "").trim();
  if (!text || text === "-") return fallback;
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Field({ label, children }) {
  return (
    <div>
      <div className="agent-booking-field-label">{label}</div>
      <div className="agent-booking-field-value">{children || "-"}</div>
    </div>
  );
}

export default function SupportTicketDetailPage() {
  const { ticketId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const payload = await agentApi.get(`/travel-partner/support-tickets/${ticketId}`);
    setDetail(unwrapData(payload));
  }

  useEffect(() => {
    let alive = true;
    load()
      .catch((error) => {
        if (alive) showApiError(error, "LOAD_SUPPORT_TICKET");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [ticketId]);

  const ticket = detail?.ticket || {};
  const comments = Array.isArray(detail?.comments) ? detail.comments : [];
  const attachments = Array.isArray(detail?.attachments) ? detail.attachments : [];
  const closed = String(ticket.status || "").toLowerCase() === "closed";

  async function addComment() {
    if (closed) {
      showErrorAction("TICKET_CLOSED");
      return;
    }
    if (!comment.trim()) {
      showErrorAction("TICKET_COMMENT_REQUIRED");
      return;
    }
    try {
      setSaving(true);
      await agentApi.post(`/travel-partner/support-tickets/${ticketId}/comments`, { body: { body: comment } });
      setComment("");
      showSuccessAction("ADD_TICKET_COMMENT");
      await load();
    } catch (error) {
      showApiError(error, "ADD_TICKET_COMMENT");
    } finally {
      setSaving(false);
    }
  }

  async function uploadAttachment(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (closed) {
      showErrorAction("TICKET_CLOSED");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showErrorAction("DOCUMENT_TOO_LARGE");
      return;
    }
    const formData = new FormData();
    formData.append("attachment", file);
    try {
      await agentApi.post(`/travel-partner/support-tickets/${ticketId}/attachments`, { body: formData });
      showSuccessAction("UPLOAD_TICKET_ATTACHMENT");
      await load();
    } catch (error) {
      showApiError(error, "UPLOAD_TICKET_ATTACHMENT");
    }
  }

  async function downloadAttachment(attachment) {
    const blob = await agentApi.get(
      `/travel-partner/support-tickets/${ticketId}/attachments/${attachment.attachment_id}/download`,
      { responseType: "blob" }
    );
    downloadBlob(blob, attachment.original_file_name);
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Support"
          title={ticket.ticket_number || "Ticket"}
          description={ticket.subject}
          items={[DASHBOARD_CRUMB, { label: "Support", href: "/support" }, { label: ticket.ticket_number || "Ticket" }]}
          backTo="/support"
          actions={ticket.status ? <StatusBadge value={ticket.status} /> : null}
        />

        {loading ? <PageLoader message="Loading ticket..." /> : null}

        {!loading && !ticket.ticket_id ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">Ticket not found.</div>
          </div>
        ) : null}

        {!loading && ticket.ticket_id ? (
          <>
            {closed ? (
              <div className="alert alert-secondary mb-4">
                This ticket is closed. You can view comments and attachments only.
              </div>
            ) : null}

            <div className="card border-0 shadow-sm mb-4 agent-booking-header-card">
              <div className="card-body">
                <div className="agent-booking-header-meta">
                  <Field label="Priority"><StatusBadge value={ticket.priority} /></Field>
                  <Field label="Category">{formatLabel(ticket.category)}</Field>
                  <Field label="Booking">{ticket.booking_reference || "-"}</Field>
                  <Field label="Updated">{formatDateTime(ticket.updated_date)}</Field>
                </div>
                {ticket.description ? (
                  <p className="text-secondary mb-0 mt-4" style={{ whiteSpace: "pre-wrap" }}>{ticket.description}</p>
                ) : null}
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h5 className="agent-booking-section-title">Comments</h5>
                    {comments.length ? comments.map((item) => (
                      <div key={item.comment_id} className="border-bottom py-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                          <div className="fw-semibold">{item.author_name || "Support"}</div>
                          <div className="small text-secondary">{formatDateTime(item.created_date)}</div>
                        </div>
                        <div className="small text-secondary mb-2">{formatLabel(item.author_role, "")}</div>
                        <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{item.body}</p>
                      </div>
                    )) : <p className="text-secondary mb-0">No comments yet.</p>}

                    {!closed ? (
                      <div className="mt-3">
                        <label className="form-label" htmlFor="ticket-comment">Add a comment</label>
                        <textarea
                          id="ticket-comment"
                          className="form-control"
                          rows={3}
                          value={comment}
                          onChange={(event) => setComment(event.target.value)}
                        />
                        <button type="button" className="btn btn-primary btn-sm mt-2" disabled={saving} onClick={addComment}>
                          {saving ? "Saving..." : "Add comment"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h5 className="agent-booking-section-title">Attachments</h5>
                    {!closed ? (
                      <input type="file" className="form-control mb-3" onChange={uploadAttachment} />
                    ) : null}
                    {attachments.length ? attachments.map((item) => (
                      <div key={item.attachment_id} className="d-flex justify-content-between align-items-center gap-2 border-bottom py-2">
                        <span className="text-break">{item.original_file_name}</span>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => downloadAttachment(item)}>
                          Download
                        </button>
                      </div>
                    )) : <p className="text-secondary mb-0">No attachments.</p>}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
